<script lang="ts">
	import { locale } from '$lib/localization/locale';
	import { message, richMessage } from '$lib/localization/messages';

	import { IS_AQUEDUCT } from '$lib/site';
	import {
		allowAnalytics,
		analyticsConsent,
		closeAnalyticsChoices,
		declineAnalytics
	} from '$lib/analyticsConsent';

	const productName: string = IS_AQUEDUCT ? 'Aqueduct' : 'Iliad';
</script>

{#if $analyticsConsent.choicesOpen && $analyticsConsent.status !== 'checking'}
	<aside
		class="analytics-banner"
		aria-label={message($locale, 'common/AnalyticsConsentBanner', 'analyticsChoices')}
		aria-live="polite"
	>
		<div class="analytics-content">
			<div class="analytics-copy">
				<h2>{message($locale, 'common/AnalyticsConsentBanner', 'optionalAnalytics')}</h2>
				<p>
					{@html richMessage(
						$locale,
						'common/AnalyticsConsentBanner',
						'weUseAnalyticsToUnderstandHowIsUsedAnd',
						{ productName: productName }
					)}
				</p>
			</div>

			<div class="analytics-actions">
				<button class="decline" type="button" on:click={declineAnalytics}
					>{message($locale, 'common/AnalyticsConsentBanner', 'decline')}</button
				>
				<button class="allow" type="button" on:click={allowAnalytics}
					>{message($locale, 'common/AnalyticsConsentBanner', 'allowAnalytics')}</button
				>
				{#if $analyticsConsent.status !== 'choice-required'}
					<button
						class="close"
						type="button"
						aria-label={message($locale, 'common/AnalyticsConsentBanner', 'closeAnalyticsChoices')}
						on:click={closeAnalyticsChoices}>×</button
					>
				{/if}
			</div>
		</div>
	</aside>
{/if}

<style>
	.analytics-banner {
		position: fixed;
		z-index: 10000;
		right: 1rem;
		bottom: 1rem;
		left: 1rem;
		font-family: inherit;
		pointer-events: none;
	}

	.analytics-content {
		display: flex;
		width: min(100%, 38rem);
		margin: 0 auto;
		padding: 1rem;
		align-items: center;
		gap: 1.25rem;
		border: 1px solid #3f3f46;
		border-radius: 0.25rem;
		background: rgba(16, 16, 22, 0.98);
		box-shadow: 0 18px 48px rgba(0, 0, 0, 0.35);
		color: #d4d4d8;
		pointer-events: auto;
	}

	.analytics-copy {
		flex: 1;
		min-width: 0;
	}

	h2 {
		margin: 0 0 0.25rem;
		color: #fafafa;
		font-size: 0.875rem;
		font-weight: 600;
		line-height: 1.4;
	}

	p {
		margin: 0;
		font-size: 0.8125rem;
		line-height: 1.5;
	}

	:global(.analytics-banner a) {
		color: #67e8f9;
		text-underline-offset: 0.15em;
	}

	.analytics-actions {
		display: flex;
		flex: 0 0 auto;
		align-items: center;
		gap: 0.5rem;
	}

	button {
		min-height: 2.25rem;
		padding: 0.45rem 0.75rem;
		border-radius: 0.25rem;
		font: inherit;
		font-size: 0.8125rem;
		font-weight: 600;
		cursor: pointer;
	}

	button:focus-visible,
	:global(.analytics-banner a:focus-visible) {
		outline: 2px solid #67e8f9;
		outline-offset: 2px;
	}

	.decline {
		border: 1px solid #52525b;
		background: transparent;
		color: #e4e4e7;
	}

	.decline:hover {
		background: #27272a;
	}

	.allow {
		border: 1px solid #22d3ee;
		background: #22d3ee;
		color: #083344;
	}

	.allow:hover {
		border-color: #67e8f9;
		background: #67e8f9;
	}

	.close {
		width: 2.25rem;
		padding: 0;
		border: 0;
		background: transparent;
		color: #a1a1aa;
		font-size: 1.25rem;
		font-weight: var(--font-weight-regular);
	}

	.close:hover {
		color: #fafafa;
	}

	@media (max-width: 640px) {
		.analytics-banner {
			right: 0.75rem;
			bottom: 0.75rem;
			left: 0.75rem;
		}

		.analytics-content {
			align-items: stretch;
			flex-direction: column;
			gap: 0.875rem;
		}

		.analytics-actions {
			display: grid;
			grid-template-columns: 1fr 1fr auto;
		}
	}
</style>
